import { useEffect, useMemo, useState } from 'react'

import { TransactionType } from '@src/api/gas_estimation.api'
import { ChainId } from '@src/configs/chainInfo'
import { MAX_TOKEN_DISPLAY_DECIMALS } from '@src/constants/exchange'
import { useFetchServerlessGasEstimates } from '@src/hooks/useFetchServerlessGasEstimates'
import { useExchangeState } from '@src/pages/Exchange/context/ExchangeStateContext'
import useExchangeBalances from '@src/pages/Exchange/hooks/useExchangeBalances'
import { IExchangeData } from '@src/types/exchange'
import { isPotentiallyCCTP } from '@src/utils'
import { Balance } from '@src/utils/balance'
import { isFeatherChain } from '@src/utils/chainUtils'
import { createContextLogger } from '@src/utils/logger'
import { useTokensStore } from '@src/zustand_stores'

const logger = createContextLogger('useServerlessGasEstimation')

interface UseServerlessGasEstimationParams {
    sellData: IExchangeData
    buyData: IExchangeData
    sellAllowance: bigint | undefined
    isWrapOrUnwrapOnly: boolean
}

export function useServerlessGasEstimation({
    sellData,
    buyData,
    sellAllowance,
    isWrapOrUnwrapOnly,
}: UseServerlessGasEstimationParams) {
    // This is already defined in useExchangeMemos but it would be cumbersome to remove from there.
    const isCCTP = useMemo(
        () => isPotentiallyCCTP(sellData?.token?.symbol, buyData?.token?.symbol, sellData?.chainId, buyData?.chainId),
        [buyData?.chainId, buyData?.token?.symbol, sellData?.chainId, sellData?.token?.symbol]
    )

    // Check if this is a Feather order by checking if the destination chain is a Feather chain
    const isFeatherOrder = useMemo(() => isFeatherChain(buyData?.chainId), [buyData?.chainId])

    const [gasFeeString, setGasFeeString] = useState<string>('Loading...')
    const [totalCostInGasToken, setTotalCostInGasToken] = useState<bigint>(BigInt(0))
    const getGasTokenProps = useTokensStore(state => state.getGasTokenProps)
    const { data: gasEstimatesData, isLoading: isGasEstimatesLoading } = useFetchServerlessGasEstimates()

    // Extract isGasEstimationFetched into a memo that checks if data exists
    // Consider it ready right after the first fetch, regardless of whether new data is loading
    // This allows background refreshes without affecting the UI state
    const isGasEstimationFetched = useMemo(() => !!gasEstimatesData?.data, [gasEstimatesData?.data])

    const { nativeBalance, isNativeBalanceLoading } = useExchangeBalances()
    const { setHasInsufficientGas, setGasAmountNeededString } = useExchangeState()

    // Helper function to format the total gas needed as a string
    const formatTotalGasNeeded = (sellData: IExchangeData, totalGas: bigint): string => {
        try {
            return `${new Balance(getGasTokenProps(sellData.chainId)).setValue(totalGas).formatWithCommas(MAX_TOKEN_DISPLAY_DECIMALS)} ${getGasTokenProps(sellData.chainId).symbol}`
        } catch {
            return ''
        }
    }

    useEffect(() => {
        // Skip gas estimation for Feather orders
        if (isFeatherOrder) {
            setGasFeeString('Included')
            setTotalCostInGasToken(BigInt(0))
            return
        }

        // Skip calculation if we don't have data yet
        if (!isGasEstimationFetched || !sellData.chainId || typeof gasEstimatesData === 'undefined') {
            setGasFeeString('Loading...')
            setTotalCostInGasToken(BigInt(0))
            return
        }

        /**
         * NOTE: This is a workaround for Jupiter same-chain swaps on Solana.
         * We currently don't have gas estimation for same-chain swaps on Solana using the Jupiter DEX.
         * For this reason we now estimate it with the APPROVE transaction cost.
         */
        if (sellData.chainId === ChainId.Solana && buyData.chainId === ChainId.Solana) {
            logger.info('Solana same-chain swap detected. Using APPROVE as gas estimation (Jupiter workaround)')
            try {
                const chainIdStr = sellData.chainId.toString()
                const chainData = gasEstimatesData?.data[chainIdStr]

                if (chainData && chainData.results[TransactionType.APPROVE]) {
                    // Use only the APPROVE transaction cost for Solana same-chain swaps
                    const approveGasCost = chainData.results[TransactionType.APPROVE].costInGasToken
                    setTotalCostInGasToken(approveGasCost)

                    // Format the gas fee string
                    if (chainData.results[TransactionType.APPROVE].costInUSD !== null) {
                        setGasFeeString(
                            `$${Math.round(1000000 * chainData.results[TransactionType.APPROVE].costInUSD) / 1000000}`
                        )
                    } else {
                        setGasFeeString(formatTotalGasNeeded(sellData, approveGasCost))
                    }

                    // Set gas amount needed string for UI
                    setGasAmountNeededString(formatTotalGasNeeded(sellData, approveGasCost))

                    // Check if user has sufficient gas
                    if (nativeBalance && approveGasCost > nativeBalance.bigintValue) {
                        logger.info('Native balance is insufficient for Solana same-chain swap.')
                        setHasInsufficientGas(true)
                    } else if (nativeBalance === undefined) {
                        logger.error('Native balance is undefined.')
                        setHasInsufficientGas(true)
                    } else {
                        logger.success('Native balance is ok for Solana same-chain swap.')
                        setHasInsufficientGas(false)
                    }
                    return
                }
            } catch (error) {
                logger.error('Error computing Solana same-chain swap gas fees:', error)
            }
            // If we reach here, something went wrong with the special case handling
            // Fall back to the standard calculation below
        }

        try {
            // Safe to use non-null assertion here as we've confirmed data exists in the isGasEstimationFetched memo
            const chainIdStr = sellData.chainId.toString()
            const chainData = gasEstimatesData?.data[chainIdStr]

            // Handle case when chain data is not available
            if (!chainData) {
                logger.error(`No gas estimation data available for chain ID ${sellData.chainId}`)
                setGasFeeString('Error')
                setTotalCostInGasToken(BigInt(0))
                return
            }

            // Calculate the total gas cost based on transaction types needed
            let calculatedTotalCostInGasToken = BigInt(0)
            let totalCostInUSD: number | null = 0
            const transactionTypes: TransactionType[] = []

            // Determine which transaction types to include based on parameters
            if (isWrapOrUnwrapOnly) {
                transactionTypes.push(TransactionType.WRAP)
            } else if (isCCTP) {
                const isApprovalNeeded =
                    sellData.token.native === false &&
                    typeof sellAllowance !== 'undefined' &&
                    sellAllowance < sellData.value

                if (isApprovalNeeded) {
                    transactionTypes.push(TransactionType.APPROVE)
                }
                transactionTypes.push(TransactionType.DEPOSIT_FOR_BURN)
            } else {
                const isApprovalNeeded =
                    sellData.token.native === false &&
                    typeof sellAllowance !== 'undefined' &&
                    sellAllowance < sellData.value

                if (sellData.token.native) {
                    // For native token, we need wrap, approve, and place order
                    transactionTypes.push(TransactionType.WRAP)
                    transactionTypes.push(TransactionType.APPROVE)
                    transactionTypes.push(TransactionType.PLACE_ORDER)
                } else {
                    // For non-native token, we need approve (if needed) and place order
                    if (isApprovalNeeded) {
                        transactionTypes.push(TransactionType.APPROVE)
                    }
                    transactionTypes.push(TransactionType.PLACE_ORDER)
                }
            }

            // Check if we need to handle CCTP with missing DEPOSIT_FOR_BURN data
            let needToApproximateCCTP = false
            if (isCCTP && transactionTypes.includes(TransactionType.DEPOSIT_FOR_BURN)) {
                // Check if DEPOSIT_FOR_BURN is missing or has zero cost or has a simulation error
                if (
                    !chainData.results[TransactionType.DEPOSIT_FOR_BURN] ||
                    chainData.results[TransactionType.DEPOSIT_FOR_BURN].costInGasToken === BigInt(0) ||
                    chainData.results[TransactionType.DEPOSIT_FOR_BURN].simulationError
                ) {
                    // Check if PLACE_ORDER is available for approximation
                    if (chainData.results[TransactionType.PLACE_ORDER]) {
                        needToApproximateCCTP = true
                        logger.warn(`Approximating DEPOSIT_FOR_BURN with PLACE_ORDER for chain ${sellData.chainId}`)
                    }
                }
            }

            // Sum up the costs for all required transaction types
            for (const txType of transactionTypes) {
                // Special case for DEPOSIT_FOR_BURN in CCTP when we need to approximate
                if (txType === TransactionType.DEPOSIT_FOR_BURN && needToApproximateCCTP) {
                    // Use PLACE_ORDER as approximation
                    const placeOrderData = chainData.results[TransactionType.PLACE_ORDER]
                    const multiplier = sellData.token.native
                        ? BigInt(chainData.setMaxMultiplierBasisPoints)
                        : BigInt(10000)

                    const adjustedCost = (placeOrderData.costInGasToken * multiplier) / BigInt(10000)
                    calculatedTotalCostInGasToken += adjustedCost

                    // Add USD cost if available
                    if (placeOrderData.costInUSD !== null) {
                        const adjustedUsdCost = ((placeOrderData.costInUSD || 0) * Number(multiplier)) / 10000
                        totalCostInUSD = (totalCostInUSD || 0) + adjustedUsdCost
                    }
                } else if (chainData.results[txType]) {
                    // Standard case - use the actual transaction cost
                    // Apply the gas multiplier for more accurate estimation
                    // This is especially important for native tokens
                    // 10000 = 1x multiplier (100%)
                    const multiplier = sellData.token.native
                        ? BigInt(chainData.setMaxMultiplierBasisPoints)
                        : BigInt(10000)

                    // Multiply before division to maintain precision with BigInt
                    const adjustedCost = (chainData.results[txType].costInGasToken * multiplier) / BigInt(10000)
                    calculatedTotalCostInGasToken += adjustedCost

                    // Add USD cost if available
                    if (chainData.results[txType].costInUSD !== null) {
                        const adjustedUsdCost =
                            ((chainData.results[txType].costInUSD || 0) * Number(multiplier)) / 10000
                        totalCostInUSD = (totalCostInUSD || 0) + adjustedUsdCost
                    }
                } else {
                    logger.warn(`Missing ${txType} gas estimation for chain ${sellData.chainId}`)
                }
            }

            // Format the gas fee string based on the calculated costs
            // Save the calculated total cost in gas token for external use
            setTotalCostInGasToken(calculatedTotalCostInGasToken)

            if (totalCostInUSD !== null && totalCostInUSD > 0) {
                // Round to 6 decimal places for display
                setGasFeeString(`$${Math.round(1000000 * totalCostInUSD) / 1000000}`)
            } else {
                // Fall back to showing the gas token amount if USD value is not available
                setGasFeeString(formatTotalGasNeeded(sellData, calculatedTotalCostInGasToken))
            }

            // Set gas amount needed string for UI
            setGasAmountNeededString(formatTotalGasNeeded(sellData, calculatedTotalCostInGasToken))

            // Check if user has sufficient gas
            if (nativeBalance && calculatedTotalCostInGasToken > nativeBalance.bigintValue) {
                logger.info('Native balance is insufficient to execute swaps.')
                setHasInsufficientGas(true)
            } else if (nativeBalance === undefined) {
                logger.error('Native balance is undefined.')
                setHasInsufficientGas(true)
            } else {
                logger.success('Native balance is ok.')
                setHasInsufficientGas(false)
            }
        } catch (error) {
            logger.error('Error computing gas fees from serverless data:', error)
            setGasFeeString('Error')
            setTotalCostInGasToken(BigInt(0))
        }
    }, [
        gasEstimatesData,
        isGasEstimationFetched, // Updated dependency to use the memo instead of the loading state
        nativeBalance,
        isNativeBalanceLoading,
        sellData.chainId,
        sellData.token,
        sellData.token.native,
        sellAllowance,
        isCCTP,
        isWrapOrUnwrapOnly,
        isFeatherOrder,
    ])

    return { gasFeeString, isGasEstimationFetched, totalCostInGasToken }
}
